import { useCallback, useEffect, useRef } from "react";

const SCROLL_THRESHOLD = 80;

export const useAutoScroll = (
  deps: unknown[],
  options?: { paused?: boolean },
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  const smoothScrollPendingRef = useRef(false);
  const hardPauseRef = useRef(false);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledRef.current = distanceFromBottom > SCROLL_THRESHOLD;
  }, []);

  const container = containerRef.current;
  useEffect(() => {
    if (!container) {
      return;
    }
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll, container]);

  useEffect(() => {
    if (
      options?.paused ||
      hardPauseRef.current ||
      userScrolledRef.current ||
      smoothScrollPendingRef.current
    ) {
      return;
    }
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.paused, ...deps]);

  const suppressAutoScroll = useCallback(() => {
    smoothScrollPendingRef.current = true;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    userScrolledRef.current = false;
    smoothScrollPendingRef.current = false;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  const pauseAutoScroll = useCallback(() => {
    hardPauseRef.current = true;
    userScrolledRef.current = true;
  }, []);

  const resumeAutoScroll = useCallback(() => {
    hardPauseRef.current = false;
  }, []);

  return {
    containerRef,
    scrollToBottom,
    suppressAutoScroll,
    pauseAutoScroll,
    resumeAutoScroll,
  };
};
